import { useEffect, useMemo, useState } from 'react'
import { Award, Check, ExternalLink, Lock, Minus } from 'lucide-react'
import { api } from '../lib/api'
import type { CertStatus } from '../lib/types'
import { useAuthed } from '../lib/session'
import { useSlots } from '../features/useSlots'
import { EVENT } from '../config/event'
import { Masthead } from '../ui/Chrome'
import { Notice, Skeleton } from '../ui/Feedback'

export function CertificateRoute() {
  const session = useAuthed()
  const { slots, totalCheckin } = useSlots()
  const [status, setStatus] = useState<CertStatus | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    api
      .certStatus(session.email)
      .then((res) => !cancelled && setStatus(res))
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [session.email])

  const required = useMemo(
    () => slots.filter((s) => Boolean(s.qr_required)).length,
    [slots],
  )

  return (
    <main className="page">
      <Masthead kicker="Your record" title="Certificate" />

      {loading ? (
        <div style={{ display: 'grid', gap: 12, paddingTop: 'var(--space-lg)' }}>
          <Skeleton height="12rem" />
          <Skeleton height="3rem" />
        </div>
      ) : failed || !status ? (
        <Notice
          icon={<Award size={22} strokeWidth={1.3} />}
          title="Could not check your certificate"
          body="The server did not respond. Try again shortly."
        />
      ) : !status.show_cert ? (
        <Notice
          icon={<Lock size={22} strokeWidth={1.3} />}
          title="Not released yet"
          body="Certificates are published by the organisers after the event closes. Keep checking in — your attendance is being recorded."
        />
      ) : (
        <CertCard
          status={status}
          attended={totalCheckin}
          required={required}
          name={session.name}
        />
      )}
    </main>
  )
}

function CertCard({
  status,
  attended,
  required,
  name,
}: {
  status: CertStatus
  attended: number
  required: number
  name: string
}) {
  const eligible = Boolean(status.cert_eligable)
  /*
   * Feedback step switched off for this event - uncomment this and the checklist item
   * below to bring it back.
   *
   * It was never a gate: `downloadable` is eligibility plus a cert_url, so nothing
   * about issuing a certificate depended on it. It only ever reported the flag that
   * /update-feedback sets, and an unticked row on a checklist reads as something the
   * delegate still has to do. `feedback_done` is still returned by the API and still
   * on the CertStatus type; only the display is gone.
   */
  // const feedbackDone = Boolean(status.feedback_done)
  const downloadable = eligible && Boolean(status.cert_url)

  return (
    <>
      <section className="cert">
        <div className="cert__seal" aria-hidden="true">
          <Award size={22} strokeWidth={1.3} />
        </div>

        <p className="kicker">Certificate of attendance</p>
        <h2 className="cert__title">{name}</h2>

        {status.attendance_exempt ? (
          <p className="kicker">Organising committee</p>
        ) : (
          <>
            <p className="cert__ratio tnum" aria-label={`${attended} of ${required} sessions`}>
              {attended}
              <span>/{required || '—'}</span>
            </p>
            <p className="kicker">Sessions recorded</p>
          </>
        )}

        <p className="cert__body">
          {status.attendance_exempt
            ? downloadable
              ? 'Attendance is not required of committee members. Your certificate is ready.'
              : 'Attendance is not required of committee members. Your certificate is being prepared and will appear here.'
            : downloadable
              ? 'You have met the attendance requirement. Your certificate is ready.'
              : eligible
                ? 'You have met the attendance requirement. Your certificate is being prepared and will appear here.'
                : `Keep scanning in at each session. You need ${Math.max(0, Math.ceil(required * EVENT.certificateAttendanceThreshold / 100) - attended)} more to qualify; the organisers then issue the certificate.`}
        </p>

        {downloadable ? (
          <div className="cert__action">
            {/*
              Opened in a new tab rather than forced as a download: the file is served
              from another origin, so a `download` attribute is ignored anyway, and a
              tab lets people view it before saving.
            */}
            <a
              href={status.cert_url!}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn--primary"
            >
              <ExternalLink size={17} strokeWidth={1.5} />
              Open certificate
            </a>
          </div>
        ) : null}
      </section>

      {/*
        * The checklist only while something on it is still outstanding.
        *
        * Once the certificate is downloadable both items are ticked, and the list
        * becomes two green ticks restating - underneath the primary action - what the
        * card said in the sentence directly above it and what the button itself proves.
        * A checklist with nothing left on it is a receipt, and it was sitting below the
        * one control this screen exists to offer.
        *
        * `!downloadable` rather than `!eligible`, because the middle state is the one
        * that needs it most: requirement met, certificate not issued yet, one tick and
        * one blank. That is the only place on this screen where the two halves of
        * "ready" are reported separately, and the card cannot say it alone.
        */}
      {!downloadable ? (
      <ul className="checklist" style={{ listStyle: 'none', padding: 0 }}>
        <ChecklistItem
          done={eligible}
          label="Attendance requirement met"
          detail={
            eligible
              ? 'Enough sessions recorded'
              : `${attended} of ${required} sessions so far`
          }
        />
        {/*
        <ChecklistItem
          done={feedbackDone}
          label="Feedback form completed"
          detail={
            feedbackDone
              ? 'Received'
              : 'Submit the organisers’ feedback form when it is sent to you'
          }
        />
        */}
        <ChecklistItem
          done={downloadable}
          label="Certificate issued"
          detail={downloadable ? 'Ready to open' : eligible ? 'The organisers issue it once attendance closes' : 'Issued by the organisers once you qualify'}
        />
      </ul>
      ) : null}
    </>
  )
}

function ChecklistItem({
  done,
  label,
  detail,
}: {
  done: boolean
  label: string
  detail: string
}) {
  return (
    <li className="checklist__item" data-done={done || undefined}>
      <span className="checklist__mark" aria-hidden="true">
        {done ? <Check size={12} strokeWidth={3} /> : <Minus size={12} strokeWidth={2} />}
      </span>
      <span>
        <strong style={{ fontWeight: 600 }}>{label}</strong>
        <br />
        <span className="faint">{detail}</span>
      </span>
    </li>
  )
}
